import React from 'react';
import { AppButton, Box, Inscription, Preloader, RoundPendingIcon, ShimmerBox } from '@common-fe/common-fe';

import { WarnModal } from '@/components/elements';

import { useBulkPlanReactivationModal } from './hooks/useBulkPlanReactivationModal';
import { BulkPlanReactivationList } from './BulkPlanReactivationList';

interface Props {
  onCancel: () => void;
}

const testId = 'BulkPlanReactivationModal';
const DESCRIPTION = `Plans will be reactivated using all prior settings. Settings can be modified after activation by editing the plan.
 Only plans with remaining dates of coverage can be reactivated.`;
const NO_PLANS_DESCRIPTION = 'Only plans with remaining dates of coverage can be reactivated. Currently, no plans meet these criteria.';

export const BulkPlanReactivationModal: React.FC<Props> = ({
  onCancel,
}) => {
  const {
    plans,
    isLoadingPlans,
    loading: isLoading,
    isServerError,
    setIsServerError,
    onConfirm,
    isNoPlans,
  } = useBulkPlanReactivationModal();

  if (isLoadingPlans) {
    return (
      <Box margin={{ top: 'spacing24' }}>
        <ShimmerBox height="270px" width="100%" />
      </Box>
    );
  }

  if (isNoPlans) {
    return (
      <Box pad={{ horizontal: 'spacing24', vertical: 'spacing24' }} data-testid={testId}>
        <Box align="center" margin={{ bottom: 'spacing12' }} data-testid={`${testId}_icon-container`}>
          <RoundPendingIcon color="warning"/>
        </Box>
        <Box margin={{ bottom: 'spacing24' }}>
          <Inscription
            size="2xl"
            weight="bold"
            color="textBody"
            textAlign="center"
            data-testid={`${testId}_title`}
          >
            No Plans Available to Reactivate
          </Inscription>
        </Box>

      <Box margin={{ bottom: 'spacing8' }}>
        <Inscription
          color="textBody"
          textAlign="center"
          data-testid={`${testId}_description`}
        >
          {NO_PLANS_DESCRIPTION}
        </Inscription>
      </Box>
  
        <Box
          direction="row"
          gap="spacing12"
          width="100%"
          data-testid={`${testId}_actions`}
          justify='center'
        >
          <Box width="200px">
            <AppButton
              testId={`${testId}_got-it-button`}
              buttonType="secondary"
              onClick={onCancel}
              width="100%"
              disabled={isLoading}
            >
              Got it!
            </AppButton>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box data-testid={testId}>
      <Box margin={{ bottom: 'spacing8' }}>
        <Inscription
          size="2xl"
          weight="bold"
          color="textBody"
          textAlign="center"
          data-testid={`${testId}_title`}
        >
          Reactivate Terminated Plans?
        </Inscription>
      </Box>

      <Box margin={{ bottom: 'spacing24' }}>
        <Inscription
          color="textBody"
          textAlign="center"
          data-testid={`${testId}_description`}
        >
          {DESCRIPTION}
        </Inscription>
      </Box>

      <Box
        pad="spacing24"
        margin={{ bottom: 'spacing24' }}
        round="moduleRound"
        background={{ color: 'module' }}
      >
        <BulkPlanReactivationList plans={plans} />
      </Box>

      <Box
        direction="row"
        gap="spacing12"
        width="100%"
        data-testid={`${testId}_actions`}
        justify='end'
      >
        <Box width="150px">
          <AppButton
            testId={`${testId}_cancel`}
            buttonType="secondary"
            onClick={onCancel}
            width="100%"
            disabled={isLoading}
          >
            Cancel
          </AppButton>
        </Box>
        <Box width="150px">
          <AppButton
            testId={`${testId}_confirm`}
            onClick={onConfirm}
            width="100%"
            disabled={isLoading}
          >
            {isLoading ? <Preloader color="white" /> : 'Confirm'}
          </AppButton>
        </Box>
      </Box>
      <WarnModal
        testId="plan-reactivation-server-error-modal"
        visible={isServerError}
        onSetVisible={setIsServerError}
        header="The server encountered an error processing the request"
        helptext={`It doesn't appear to have affected your data, but we cannot save the Plan reactivation with the latest records.\nOur technical staff have been automatically notified and will be looking into this with the utmost urgency.`}
        buttonText="Close"
        onSubmit={onConfirm}
        submitButtonText="Try again"
      />
    </Box>
  );
};
